import React, { Fragment } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  ArrowPathIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  CurrencyDollarIcon,
  InboxIcon,
} from '@heroicons/react/24/outline';
import { SystemNotification } from '../../services/api';

interface NotificationBellProps {
  chatUnreadCount: number;
  systemUnreadCount: number;
  totalUnreadCount: number;
  notifications: SystemNotification[];
  chatHref: string;
  loading?: boolean;
  size?: 'sm' | 'lg';
  className?: string;
  onRefresh: () => Promise<void> | void;
  onMarkRead: (id: number) => Promise<SystemNotification> | void;
  onMarkAllRead: () => Promise<void> | void;
}

const formatCount = (count: number) => (count > 99 ? '99+' : String(count));

const formatNotificationTime = (value: string) => {
  const date = new Date(value);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const getNotificationIcon = (notification: SystemNotification) => {
  if (notification.type === 'ORDER' || notification.type === 'DEAL') {
    return CurrencyDollarIcon;
  }

  return BellIcon;
};

const NotificationBell: React.FC<NotificationBellProps> = ({
  chatUnreadCount,
  systemUnreadCount,
  totalUnreadCount,
  notifications,
  chatHref,
  loading = false,
  size = 'sm',
  className,
  onRefresh,
  onMarkRead,
  onMarkAllRead,
}) => {
  const navigate = useNavigate();
  const iconClassName = size === 'lg' ? 'h-6 w-6' : 'h-5 w-5';

  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <Popover.Button
            className={clsx(
              'relative rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500',
              size === 'lg' ? 'p-2 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100' : 'p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100',
              className,
            )}
            title="Notifications"
          >
            <BellIcon className={iconClassName} />
            {totalUnreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-error-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white">
                {formatCount(totalUnreadCount)}
              </span>
            )}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl">
              <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-neutral-900">Notifications</p>
                  <p className="text-xs text-neutral-500">
                    {totalUnreadCount > 0 ? `${totalUnreadCount} unread` : 'All caught up'}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {systemUnreadCount > 0 && (
                    <button
                      type="button"
                      onClick={() => void onMarkAllRead()}
                      className="rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
                      title="Mark system notifications as read"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void onRefresh()}
                    className="rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 disabled:opacity-50"
                    disabled={loading}
                    title="Refresh notifications"
                  >
                    <ArrowPathIcon className={clsx('h-4 w-4', loading && 'animate-spin')} />
                  </button>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto py-2">
                {chatUnreadCount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      navigate(chatHref);
                    }}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-cyan-50"
                  >
                    <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-cyan-100 text-cyan-700">
                      <ChatBubbleLeftRightIcon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-3">
                        <span className="text-sm font-semibold text-neutral-900">Team chat</span>
                        <span className="rounded-full bg-cyan-600 px-2 py-0.5 text-xs font-semibold text-white">
                          {formatCount(chatUnreadCount)}
                        </span>
                      </span>
                      <span className="mt-0.5 block text-xs text-neutral-500">
                        Open chat to read your unread messages.
                      </span>
                    </span>
                  </button>
                )}

                {notifications.length === 0 && chatUnreadCount === 0 ? (
                  <div className="px-6 py-10 text-center">
                    <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-neutral-100 text-neutral-400">
                      <InboxIcon className="h-6 w-6" />
                    </div>
                    <p className="mt-3 text-sm font-medium text-neutral-800">No notifications</p>
                    <p className="mt-1 text-xs text-neutral-500">New chat and order activity will appear here.</p>
                  </div>
                ) : (
                  notifications.map((notification) => {
                    const Icon = getNotificationIcon(notification);
                    const isUnread = !notification.readAt;

                    return (
                      <button
                        key={notification.id}
                        type="button"
                        onClick={async () => {
                          if (isUnread) {
                            await onMarkRead(notification.id);
                          }

                          close();
                          if (notification.link) {
                            navigate(notification.link);
                          }
                        }}
                        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-neutral-50"
                      >
                        <span className={clsx(
                          'mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full',
                          isUnread ? 'bg-primary-100 text-primary-700' : 'bg-neutral-100 text-neutral-500',
                        )}>
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-start justify-between gap-3">
                            <span className={clsx(
                              'line-clamp-2 text-sm',
                              isUnread ? 'font-semibold text-neutral-950' : 'font-medium text-neutral-800',
                            )}>
                              {notification.title}
                            </span>
                            {isUnread && <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" />}
                          </span>
                          <span className="mt-0.5 line-clamp-2 text-xs text-neutral-500">
                            {notification.message}
                          </span>
                          <span className="mt-1 block text-xs text-neutral-400">
                            {formatNotificationTime(notification.createdAt)}
                          </span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default NotificationBell;
